import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAllBranches, addBranch } from '../services/operations/branchAPI';
import { getSubjectsByBranch, addSubject } from '../services/operations/subjectAPI';
import SEO from '../components/SEO';
import {
  FolderOpen,
  BookOpen,
  Plus,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle2,
  GraduationCap,
  Calendar
} from 'lucide-react';

// Rupee text-icon used where a currency icon is needed
const RupeeIcon = ({ className = '' }) => (
  <span className={`inline-flex items-center justify-center ${className}`}>₹</span>
);

// Local Notification component (replaces toast for this page)
const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  const baseClasses = 'fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg font-medium transition-all duration-300 transform backdrop-blur-sm border';
  const typeClasses = type === 'error'
    ? 'bg-red-500/10 border-red-500/30 text-red-400'
    : 'bg-green-500/10 border-green-500/30 text-green-400';
  return (
    <div className={`${baseClasses} ${typeClasses}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {type === 'error' ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <CheckCircle2 className="w-5 h-5" />
          )}
          <span>{message}</span>
        </div>
        <button onClick={onClose} className="ml-4 text-white/80 hover:text-white transition">&times;</button>
      </div>
    </div>
  );
};

const FolderCard = ({ title, subtitle, count, onClick }) => (
  <div
    onClick={onClick}
    className="group cursor-pointer relative p-5 bg-gradient-to-br from-gray-800/40 to-gray-900/40 hover:from-gray-800/60 hover:to-gray-900/60 border border-gray-700/50 hover:border-indigo-500/50 rounded-xl shadow-lg hover:shadow-xl hover:shadow-indigo-500/10 transform hover:scale-[1.02] transition-all duration-300 ease-in-out flex flex-col items-start gap-3 h-full min-h-[160px] overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:to-purple-500/5 transition-all duration-300" />
    <div className="relative w-full">
      <div className="flex items-center justify-between w-full mb-3">
        <div className="p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
          <FolderOpen className="w-6 h-6 text-indigo-400" />
        </div>
        {typeof count === 'number' && (
          <div className="text-xs font-semibold bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 px-3 py-1 rounded-full">
            {count} {count === 1 ? 'Item' : 'Items'}
          </div>
        )}
      </div>
      <div className="w-full">
        <div className="font-bold text-lg text-white group-hover:text-indigo-300 transition-colors truncate mb-1">{title}</div>
        {subtitle && <div className="text-sm text-gray-400 flex items-center gap-1.5">
          <RupeeIcon className="w-3.5 h-3.5" />
          <span>{subtitle}</span>
        </div>}
      </div>
    </div>
  </div>
);

const EmptyState = ({ message, onBack, currentTitle }) => (
  <div className="col-span-full w-full rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-12 flex flex-col items-center justify-center text-center gap-4 min-h-[300px]">
    <div className="p-4 rounded-full bg-gray-800/30 border border-gray-700/50 mb-2">
      <FolderOpen className="w-12 h-12 text-gray-500" />
    </div>
    <div className="text-xl font-semibold text-white/80">{message}</div>
    <div className="text-gray-400 text-sm">{currentTitle}</div>
    {onBack && (
      <button
        onClick={onBack}
        className="mt-4 px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-lg hover:shadow-indigo-500/20 transition-all flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Go Back to Branches
      </button>
    )}
  </div>
);

const FormInput = ({ name, value, onChange, placeholder, type = 'text', disabled = false, icon: Icon }) => (
  <div className="relative">
    {Icon && (
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <Icon className="w-5 h-5" />
      </div>
    )}
    <input
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      className={`w-full p-3 rounded-xl bg-gray-800/40 backdrop-blur-sm text-white border border-gray-700/50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 transition-all placeholder-gray-500 disabled:opacity-50 ${Icon ? 'pl-10' : ''}`}
    />
  </div>
);

const SubmitButton = ({ isLoading, children }) => (
  <button
    type="submit"
    disabled={isLoading}
    className={`w-full px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:shadow-indigo-500/20 transition-all flex items-center justify-center gap-2 ${isLoading ? 'bg-gray-600/50 text-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
  >
    {isLoading ? (
      <>
        <Loader2 className="animate-spin h-5 w-5" />
        Processing...
      </>
    ) : (
      <>
        <Plus className="w-5 h-5" />
        {children}
      </>
    )}
  </button>
);

const Branches = () => {
  const navigate = useNavigate();
  const { branchId } = useParams();

  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ message: null, type: null });
  const [newBranch, setNewBranch] = useState({ year: '', branch: '', price: '', semester: 'semester 1' });
  const [newSubject, setNewSubject] = useState({ title: '', price: '' });
  const [addingBranch, setAddingBranch] = useState(false);
  const [addingSubject, setAddingSubject] = useState(false);

  // Find the current branch title for display
  const currentBranch = useMemo(() => branches.find(b => b._id === branchId), [branches, branchId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (branchId) {
        const res = await getSubjectsByBranch(branchId);
        if (res && res.success) setSubjects(res.subjects || []);
        else throw new Error(res?.message || 'Could not fetch subjects');
      } else {
        const res = await getAllBranches();
        if (res && res.success) setBranches(res.branches || []);
        else throw new Error(res?.message || 'Could not fetch branches');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error fetching data');
      setNotification({ message: err.message || 'Failed to load data.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNewBranchChange = (e) => {
    const { name, value } = e.target;
    setNewBranch(prev => ({ ...prev, [name]: value }));
  };

  const handleAddBranch = async (e) => {
    e.preventDefault();
    if (!newBranch.branch || !newBranch.year) {
      return setNotification({ message: 'Please provide year and branch name.', type: 'error' });
    }
    setAddingBranch(true);
    try {
      // Apply naming convention for Semester 2
      const branchData = { ...newBranch };
      if (branchData.semester === 'semester 2' && !branchData.branch.toLowerCase().includes('semester 2')) {
        branchData.branch = `${branchData.branch} Semester 2`;
      }

      const res = await addBranch(branchData);
      if (res && res.success) {
        setBranches(prev => [res.branch, ...prev]);
        setNewBranch({ year: '', branch: '', price: '', semester: 'semester 1' });
        setNotification({ message: 'New Branch added successfully!', type: 'success' });
      } else {
        const msg = res?.message || 'Failed to add branch';
        setNotification({ message: msg, type: 'error' });
        console.error('Add branch failed', res);
      }
    } catch (err) {
      console.error(err);
      setNotification({ message: 'Failed to add branch due to an error.', type: 'error' });
    } finally {
      setAddingBranch(false);
    }
  };

  const handleNewSubjectChange = (e) => {
    const { name, value } = e.target;
    setNewSubject(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.title) return setNotification({ message: 'Please provide a subject title.', type: 'error' });
    setAddingSubject(true);
    try {
      const res = await addSubject(branchId, newSubject);
      if (res && res.success) {
        setSubjects(prev => [res.subject, ...prev]);
        setNewSubject({ title: '', price: '' });
        setNotification({ message: 'New Subject added successfully!', type: 'success' });
        // Re-fetch branches to update the subject count display on the main view
        if (!branchId) fetchData();
      } else {
        const msg = res?.message || 'Failed to add subject';
        setNotification({ message: msg, type: 'error' });
        console.error('Add subject failed', res);
      }
    } catch (err) {
      console.error(err);
      setNotification({ message: 'Failed to add subject due to an error.', type: 'error' });
    } finally {
      setAddingSubject(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-300 text-lg font-medium">Loading...</p>
      </div>
    </div>
  );

  return (
    <>
      <SEO
        title="Manage Branches - Admin Panel | ReferMe"
        description="Admin panel for managing branches and subjects."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification({ message: null, type: null })} />
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Header and Controls */}
          <header className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl p-6 sm:p-8 mb-8 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  {branchId ? (
                    <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                      <BookOpen className="w-6 h-6 text-indigo-400" />
                    </div>
                  ) : (
                    <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                      <GraduationCap className="w-6 h-6 text-indigo-400" />
                    </div>
                  )}
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                    {branchId ? (currentBranch ? `${currentBranch.year} - ${currentBranch.branch} Subjects` : 'Subjects') : 'Academic Branches'}
                  </h1>
                </div>
                <p className="text-sm sm:text-base text-gray-400 ml-12">
                  {branchId ? 'Manage and browse the subjects within this branch.' : 'Select an academic year/branch to view its courses, or create a new branch.'}
                </p>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                {branchId && (
                  <button
                    onClick={() => navigate('/branches')}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-gray-800/40 hover:bg-gray-800/60 text-white border border-gray-700/50 transition-all flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                )}
              </div>
            </div>
          </header>

          {/* Create Forms */}
          <div className="grid grid-cols-1 gap-6 mb-10">
            {/* Create Branch Form */}
            {!branchId && (
              <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 sm:p-8 shadow-xl">
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                    <Plus className="w-4 h-4 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Add New Academic Branch</h3>
                </div>
                <form onSubmit={handleAddBranch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <select
                      name="year"
                      value={newBranch.year}
                      onChange={handleNewBranchChange}
                      disabled={addingBranch}
                      className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 backdrop-blur-sm text-white border border-gray-700/50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 transition-all appearance-none"
                    >
                      <option value="">Select Year</option>
                      <option value="Third Year">Third Year</option>
                      <option value="Second Year">Second Year</option>
                      <option value="First Year">First Year</option>
                      <option value="Fourth Year">Fourth Year</option>
                    </select>
                  </div>
                  <FormInput name="branch" value={newBranch.branch} onChange={handleNewBranchChange} placeholder="Branch Name (e.g. IT)" disabled={addingBranch} icon={BookOpen} />
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <select
                      name="semester"
                      value={newBranch.semester}
                      onChange={handleNewBranchChange}
                      disabled={addingBranch}
                      className="w-full p-3 pl-10 rounded-xl bg-gray-800/40 backdrop-blur-sm text-white border border-gray-700/50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 transition-all appearance-none"
                    >
                      <option value="semester 1">Semester 1</option>
                      <option value="semester 2">Semester 2</option>
                    </select>
                  </div>
                  <FormInput name="price" value={newBranch.price} onChange={handleNewBranchChange} placeholder="Price" type="number" disabled={addingBranch} icon={RupeeIcon} />
                  <SubmitButton isLoading={addingBranch}>Add Branch</SubmitButton>
                </form>
              </div>
            )}

            {/* Create Subject Form */}
            {branchId && (
              <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 sm:p-8 shadow-xl">
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                    <Plus className="w-4 h-4 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Add New Subject to This Branch</h3>
                </div>
                <form onSubmit={handleAddSubject} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormInput name="title" value={newSubject.title} onChange={handleNewSubjectChange} placeholder="Subject Title (e.g. Thermodynamics)" disabled={addingSubject} icon={BookOpen} />
                  <FormInput name="price" value={newSubject.price} onChange={handleNewSubjectChange} placeholder="Price (e.g. 199)" type="number" disabled={addingSubject} icon={RupeeIcon} />
                  <SubmitButton isLoading={addingSubject}>Add Subject</SubmitButton>
                </form>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 backdrop-blur-sm p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-medium">Error</p>
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Data Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Display Branches */}
            {!branchId && branches.length > 0 && branches.map((b) => (
              <FolderCard key={b._id} title={`${b.year || ''} ${b.branch || ''}`.trim()} subtitle={`${b.price ?? 'N/A'}`} count={b.subjectCount} onClick={() => navigate(`/subjects/${b._id}`)} />
            ))}

            {/* Display Subjects */}
            {branchId && subjects.length > 0 && subjects.map((s) => (
              <FolderCard key={s._id} title={s.title} subtitle={`${s.price ?? 'N/A'}`} count={s.unitCount}
                onClick={() => navigate(`/units/${s._id}`)} />
            ))}

            {/* Empty States */}
            {!branchId && branches.length === 0 && !loading && (
              <EmptyState message="No academic branches were loaded from the API." currentTitle="Start by adding a new branch above." />
            )}

            {branchId && subjects.length === 0 && !loading && (
              <EmptyState message="No subjects found in this branch." currentTitle={currentBranch ? `${currentBranch.year} - ${currentBranch.branch}` : 'Branch detail'} onBack={() => navigate('/branches')} />
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Branches;
